import { useField, useForm } from "@taskfortress/core"
import { type Task } from "@taskfortress/core/src/clientData/contentTypes"
import { type SampleTask } from "@taskfortress/core/src/utils/randomStrings"
import React from "react"
import { Box } from "../../Box"
import { Button } from "../../Button"
import { DeleteItemButton } from "../../DeleteItemButton"
import { Text } from "../../Text"
import { DateInput, Form, TagField, TextArea, ToggleField } from "../../form"

interface TaskModalDesktopProps {
  titleField: ReturnType<typeof useField<string>>
  descriptionField: ReturnType<typeof useField<string>>
  statusField: ReturnType<typeof useField<boolean>>
  dueDateField: ReturnType<typeof useField<Date>>
  tagsField: ReturnType<typeof useField<string[]>>
  form: ReturnType<typeof useForm>
  handleFormSubmit: (e?: React.FormEvent) => void
  handleDeleteTask: () => void
  saveButtonText: string
  sampleTask: SampleTask
  tagSuggestions: string[]
  task: Task
  onDelete?: (task: Task) => void
  isVisible: boolean
}

export const TaskModalDesktop: React.FC<TaskModalDesktopProps> = ({
  titleField,
  descriptionField,
  statusField,
  dueDateField,
  tagsField,
  form,
  handleFormSubmit,
  handleDeleteTask,
  saveButtonText,
  sampleTask,
  tagSuggestions,
  task,
  onDelete,
  isVisible,
}) => {
  const handleButtonPress = () => {
    handleFormSubmit()
  }

  return (
    <Form
      onSubmit={handleFormSubmit}
      error={form.formError}
      className="h-full flex flex-col gap-4"
    >
      {/* Two Column Layout */}
      <Box className="flex-1 flex flex-row gap-6">
        {/* Left Column - Title and Description */}
        <Box className="flex-1 flex flex-col gap-4 justify-start overflow-y-scroll">
          {/* Task Title */}
          <TextArea
            {...titleField}
            label="Title"
            placeholder={sampleTask.title}
            minRows={1}
            maxRows={2}
            autofocus={isVisible && !task.title}
          />

          {/* Task Description */}
          <TextArea
            {...descriptionField}
            label="Description"
            placeholder={sampleTask.description}
            minRows={8}
            maxRows={12}
          />

          <Box className="flex-shrink-0 flex-grow-0 flex-row items-center justify-start gap-2">
            <Button variant="primary" onPress={handleButtonPress}>
              {saveButtonText}
            </Button>

            <DeleteItemButton
              onConfirm={handleDeleteTask}
              disabled={!onDelete || !task.id}
            />
          </Box>
        </Box>

        {/* Right Column - Status, Due Date, Tags */}
        <Box className="flex-0 flex flex-col gap-6 w-3/12 px-2 overflow-y-scroll overflow-x-visible">
          {/* Status Toggle */}
          <ToggleField
            value={!!statusField.value}
            handleChange={statusField.handleChange}
            label="Status"
            name="status"
            afterToggle={
              <Box className="flex flex-col gap-2">
                <Text className="text-xs text-typography-700 font-light">
                  {statusField.value ? "Done" : "Todo"}
                </Text>
              </Box>
            }
            className="mb-4"
          />

          {/* Due Date */}
          <DateInput
            {...dueDateField}
            label="Due date"
            inputContainerClassName="border-0"
            inputClassName="p-0"
          />

          {/* Tags */}
          <TagField {...tagsField} suggestions={tagSuggestions} label="Tags" />
        </Box>
      </Box>
    </Form>
  )
}
